import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '@/redux/store'

// ──────────────────────────────────────────────────────────
// Charter REST state
// ──────────────────────────────────────────────────────────

export const selectCharterConfig = (state: RootState) => state.charter.config
export const selectCharterSession = (state: RootState) => state.charter.session
export const selectMyPrepProfile = (state: RootState) => state.charter.myProfile
export const selectTeamStatuses = (state: RootState) => state.charter.teamStatuses

export const selectUnassignedMembers = (state: RootState) =>
  state.charter.unassignedMembers
export const selectTeamProfiles = (state: RootState) => state.charter.teamProfiles
export const selectRenegotiationRequest = (state: RootState) => state.charter.renegotiationRequest
export const selectCharterVersions = (state: RootState) => state.charter.charterVersions
export const selectCharterGateStatus = (state: RootState) => state.charter.gateStatus
export const selectCharterIsLoading = (state: RootState) => state.charter.isLoading
export const selectCharterRoundControlLoading = (state: RootState) =>
  state.charter.roundControlLoading
export const selectCharterRoundStatus = (state: RootState) =>
  state.charter.config?.round_status ?? 'not_started'
export const selectCharterError = (state: RootState) => state.charter.error

// ──────────────────────────────────────────────────────────
// Phase 1 — Prep WebSocket state
// ──────────────────────────────────────────────────────────

export const selectPrepWsState = (state: RootState) => state.charterPrepWS
export const selectPrepMessages = (state: RootState) => state.charterPrepWS.messages
export const selectPrepIsConnected = (state: RootState) => state.charterPrepWS.isConnected
export const selectPrepIsStreaming = (state: RootState) => state.charterPrepWS.isStreaming
export const selectCompletedSections = (state: RootState) => state.charterPrepWS.completedSections
export const selectActiveSectionKey = (state: RootState) => state.charterPrepWS.activeSectionKey
export const selectPrepProfile = (state: RootState) => state.charterPrepWS.profile
export const selectProfileApprovalRequired = (state: RootState) => state.charterPrepWS.profileApprovalRequired
export const selectSharingMode = (state: RootState) => state.charterPrepWS.sharingMode
export const selectSharingModeSelected = (state: RootState) => state.charterPrepWS.sharingModeSelected
export const selectVisibleSections = (state: RootState) => state.charterPrepWS.visibleSections
export const selectSectionOrder = (state: RootState) => state.charterPrepWS.sectionOrder
export const selectPrepStatus = (state: RootState) => state.charterPrepWS.status
export const selectPrepError = (state: RootState) => state.charterPrepWS.error
export const selectIsGeneratingProfile = (state: RootState) => state.charterPrepWS.isGeneratingProfile
export const selectProfileApproved = (state: RootState) => state.charterPrepWS.profileApproved

// ──────────────────────────────────────────────────────────
// Phase 2 & 3 — Group WebSocket state
// ──────────────────────────────────────────────────────────

export const selectGroupMessages = (state: RootState) => state.charterGroupWS.messages
export const selectGroupPagination = (state: RootState) => state.charterGroupWS.pagination
export const selectGroupIsLoadingMore = (state: RootState) => state.charterGroupWS.isLoadingMore
export const selectGroupIsConnected = (state: RootState) => state.charterGroupWS.isConnected
export const selectGroupIsStreaming = (state: RootState) => state.charterGroupWS.isStreaming
export const selectCurrentStage = (state: RootState) => state.charterGroupWS.currentStage
export const selectTurnTracker = (state: RootState) => state.charterGroupWS.turnTracker
export const selectRatificationVotes = (state: RootState) => state.charterGroupWS.ratificationVotes
export const selectRatifiedCharter = (state: RootState) => state.charterGroupWS.ratifiedCharter
export const selectCharterVersion = (state: RootState) => state.charterGroupWS.charterVersion
export const selectCharterStatus = (state: RootState) => state.charterGroupWS.charterStatus
export const selectRenegotiationMode = (state: RootState) => state.charterGroupWS.renegotiationMode
export const selectPendingRenegotiationRequest = (state: RootState) => state.charterGroupWS.pendingRenegotiationRequest
export const selectTeamName = (state: RootState) => state.charterGroupWS.teamName
export const selectGroupError = (state: RootState) => state.charterGroupWS.error
export const selectIsModeratorThinking = (state: RootState) =>
  state.charterGroupWS.isModeratorThinking
export const selectRatificationPromptActive = (state: RootState) =>
  state.charterGroupWS.ratificationPromptActive
export const selectRatificationFinalizing = (state: RootState) =>
  state.charterGroupWS.ratificationFinalizing

// ──────────────────────────────────────────────────────────
// Derived / memoized selectors
// ──────────────────────────────────────────────────────────

/** True if all 11 sections are completed */
export const selectAllSectionsCompleted = createSelector(
  selectSectionOrder,
  selectCompletedSections,
  (sectionOrder, completedSections) => {
    if (!sectionOrder.length) return false
    return sectionOrder.every((key) => Boolean(completedSections[key]))
  }
)

/** Number of completed sections */
export const selectCompletedSectionCount = createSelector(
  selectCompletedSections,
  (completedSections) => Object.keys(completedSections).length
)

/** Total sections in order */
export const selectTotalSectionCount = createSelector(
  selectSectionOrder,
  (sectionOrder) => sectionOrder.length
)

/** Ratification: how many members have voted yes */
export const selectRatificationYesCount = (state: RootState) =>
  state.charterGroupWS.ratificationVoteCount

export const selectTotalGroupMembers = (state: RootState) =>
  state.charterGroupWS.totalMembers

/** Whether charter builder feature is enabled in config */
export const selectCharterEnabled = createSelector(
  selectCharterConfig,
  (config) => config?.enabled ?? false
)

/** Effective ratified charter: WS live state first, then REST session fallback */
export const selectEffectiveRatifiedCharter = createSelector(
  selectRatifiedCharter,
  selectCharterSession,
  (wsCharter, session) => wsCharter ?? session?.ratified_charter ?? null
)

